//import { ESPLoader } from "esp-web-flasher";
import { ESPLoader } from "adafruit-webserial-esptool";
import JSZip from "jszip";
import PartitionTable from "./PartitionTable";
import RICUpdateFileType from "./RICUpdateFileType";

// RICUpdater handles update over the serial port
export default class RICUpdater {

    // The following is an example from esptool with Marty2R2
    // esptool.py -p COM6 -b 2000000 --before default_reset --after hard_reset --chip esp32 write_flash --flash_mode dio --flash_size detect --flash_freq 40m 
    //  0x1000 builds\Marty2R2\bootloader\bootloader.bin 
    //  0x8000 builds\Marty2R2\partition_table\partition-table.bin
    //  0x01e000 builds\Marty2R2\ota_data_initial.bin 
    //  0x020000 builds\Marty2R2\RICFirmware.bin 
    //  0x620000 builds\Marty2R2\spiffs.bin

    // Mapping of update file types to partition table
    private _partitionMapping = {
        spiffs: "spiffs",
        otadata: "otadata",
        main: "app0",
    }
    private _partitionOffsets = {
        bootloader: 0x1000,
        partitionTable: 0x8000,
    }

    // Percent progress at start of flashing
    private PROGRESS_PC_AT_DOWNLOAD_START = 2;
    private PROGRESS_PC_AT_DOWNLOAD_END = 10;
    private PROGRESS_PC_AT_FLASH_START = 12;
    private PROGRESS_PC_AT_FLASH_END = 70;
    private PROGRESS_PC_AT_END = 75
    private PROGRESS_PC_ERASE_ADDITION = 15;

    // Baud rate to use after bootloader has been flashed
    BAUD_RATE_AFTER_BOOTLOADER = 1500000;

    // DEFAULT_TIMEOUT for serial comms
    DEFAULT_TIMEOUT = 30000;

    // Constructor
    constructor(private _espLoader: ESPLoader,
        private _espLoaderStub: any | null,
        private _eraseFirst: boolean,
        private _onProgress: (state: string, completePC: number, isOk: boolean) => void) {
    }

    // Update function - all of the action occurs in here
    public async update(updatePath: string) {

        // Validate
        if (!this._espLoader || !this._espLoaderStub) {
            this._onProgress(`Problem communicating, please refresh the page, check the cable and try again`, 0, false);
            return;
        }

        // Debug
        console.log(`RICUpdater: update: ${updatePath} FlashSize: ${this._espLoader.flashSize}`);

        // Progress
        this._onProgress("Checking update information", 0, true);

        // Fetch the manifest
        // const manifest = await fetch(updatePath, {method: 'GET', mode: 'cors', headers: {'Content-Type': 'text/plain'}});
        const manifestResponse = await fetch(updatePath);
        const manifest = await manifestResponse.json();

        // console.log(`RICUpdater: update: manifestJson: ${JSON.stringify(manifestJson)}`);

        // Promises of files to download
        const fileInfoRecords: Array<RICUpdateFileType> = manifest.files.filter((fileInfo: RICUpdateFileType) => {
            return fileInfo.updaters.includes("OTC") || fileInfo.updaters.includes("otc");
        });

        // Debug
        for (const fileInfo of fileInfoRecords) {
            console.log(`RICUpdater: update: fileInfo: ${JSON.stringify(fileInfo)}`);
        }

        // Start fetching each of the files in parallel
        const filePromises = fileInfoRecords.map(async (fileInfo: RICUpdateFileType) => {
            const url = new URL(fileInfo.downloadUrl).toString();
            const resp = await fetch(url);
            if (!resp.ok) {
                throw new Error(
                    `Downlading firmware ${fileInfo.downloadUrl} failed: ${resp.status}`
                );
            }
            return resp.arrayBuffer();
        });

        // Debug
        console.log(`RICUpdater: update: fileInfos ${fileInfoRecords.length} filePromises ${filePromises.length}`);

        // Progress
        this._onProgress("Downloading files", this.PROGRESS_PC_AT_DOWNLOAD_START, true);

        // Download files
        let totalSize = 0;
        const zippedFilePromises = new Array<Promise<Uint8Array>>(filePromises.length);
        for (let i = 0; i < filePromises.length; i++) {
            try {
                const data = await filePromises[i];
                if (!(data instanceof ArrayBuffer)) {
                    throw new Error("Downloaded data is not an ArrayBuffer");
                }
                fileInfoRecords[i].binaryData = new Uint8Array(data);
                totalSize += data.byteLength;

                // Progress
                const progressPC = this.PROGRESS_PC_AT_DOWNLOAD_START + (this.PROGRESS_PC_AT_DOWNLOAD_END - this.PROGRESS_PC_AT_DOWNLOAD_START) * (i + 1) / filePromises.length;
                this._onProgress("Downloading files", progressPC, true);

                // Debug
                console.log(`RICUpdater: update: ${fileInfoRecords[i].elemType} len ${data.byteLength} total ${totalSize}`);

                // Zip the file just to estimate the size
                const zip = new JSZip();
                zip.file(fileInfoRecords[i].elemType, data);
                zippedFilePromises[i] = zip.generateAsync({ type: "uint8array", compression: 'DEFLATE' });

            } catch (err: any) {
                console.log(`RICUpdater: update: error: ${err}`);
                return;
            }
        }

        // Wait for zipped size
        const zippedFileContents = await Promise.all(zippedFilePromises);
        for (let i = 0; i < zippedFileContents.length; i++) {
            fileInfoRecords[i].zippedSize = zippedFileContents[i].byteLength;
            console.log(`RICUpdater: update: ${fileInfoRecords[i].elemType} zippedSize ${zippedFileContents[i].byteLength}`);
        }
        const zippedSize = zippedFileContents.reduce((acc, cur) => acc + cur.byteLength, 0);
        console.log(`RICUpdater: update: zippedFileContents ${zippedSize}`);

        // Locate the partition table file
        const partitionTableHandler = new PartitionTable();
        for (let i = 0; i < fileInfoRecords.length; i++) {
            const fileInfo = fileInfoRecords[i];
            if (fileInfo.elemType === "partitionTable") {
                partitionTableHandler.process(fileInfo);
            }
        }
        if (!partitionTableHandler.parsedOk) {
            this._onProgress(`Could not find partition table`, 0, false);
            return;
        }

        // Check the offset of each file in the partition table
        for (let i = 0; i < fileInfoRecords.length; i++) {
            const fileInfo = fileInfoRecords[i];
            if (fileInfo.elemType in this._partitionMapping) {
                const partitionName = (this._partitionMapping as any)[fileInfo.elemType];
                const partition = partitionTableHandler.getPartitionInfo(partitionName);
                if (partition !== undefined) {
                    fileInfo.flashOffset = partition.offset;
                }
            } else if (fileInfo.elemType in this._partitionOffsets) {
                fileInfo.flashOffset = (this._partitionOffsets as any)[fileInfo.elemType];
            }
            console.log(`RICUpdater: update: ${fileInfo.elemType} offset 0x${fileInfo.flashOffset.toString(16)}`);
        }

        // Sort the files in order of flash offset
        fileInfoRecords.sort((a, b) => {
            return a.flashOffset - b.flashOffset;
        });

        // Debug
        for (const fileInfo of fileInfoRecords) {
            console.log(`RICUpdater: update: fileInfo: ${fileInfo.destname} offset 0x${fileInfo.flashOffset.toString(16)}`);
        }

        // Erase first
        if (this._eraseFirst) {
            this._onProgress("Erasing flash, this may take up to 2 minutes, please wait", this.PROGRESS_PC_AT_DOWNLOAD_END, true);
            await this._espLoaderStub.eraseFlash();
        }

        // Progress
        const curProgress = this.PROGRESS_PC_AT_FLASH_START + (this._eraseFirst ? this.PROGRESS_PC_ERASE_ADDITION : 0)
        this._onProgress("Flashing firmware", curProgress, true);

        // Debug
        console.log(`RICUpdater: totalSize ${totalSize} zippedSize ${zippedSize} fileInfos ${fileInfoRecords.length}`);

        // Up the baud rate
        await this._espLoaderStub.setBaudrate(this.BAUD_RATE_AFTER_BOOTLOADER);

        // Flash files
        let lastPct = 0;
        let totalZippedWritten = 0;
        for (const fileInfo of fileInfoRecords) {
            try {

                // Debug
                console.log(`RICUpdater: update: flashing ${fileInfo.destname} offset 0x${fileInfo.flashOffset.toString(16)} len 0x${fileInfo.binaryData.byteLength.toString(16)}`);

                // Debug check specific files
                // const filesToSkip = []
                // if (filesToSkip.includes(fileInfo.elemType)) {
                //     continue;
                // }

                // Flash the file
                await this._espLoaderStub.flashData(
                    fileInfo.binaryData,
                    // eslint-disable-next-line no-loop-func
                    (bytesWritten: number) => {
                        const newPct = Math.floor(
                            ((totalZippedWritten + (bytesWritten / fileInfo.binaryData.byteLength) * fileInfo.zippedSize) / zippedSize) * 100
                        );
                        if (newPct === lastPct) {
                            return;
                        }
                        lastPct = newPct;
                        const progressPC = curProgress + newPct * (this.PROGRESS_PC_AT_FLASH_END - curProgress) / 100;
                        this._onProgress(`Writing firmware file ${fileInfo.elemType}`, progressPC, true);
                        console.log(`RICUpdater: update: ${fileInfo.elemType} ${totalZippedWritten} ${bytesWritten} ${totalZippedWritten + bytesWritten} ${zippedSize} ${newPct}% ${progressPC}`);
                    },
                    fileInfo.flashOffset,
                    true
                );

            } catch (err: any) {
                console.log(`RICUpdater: update: error: ${err}`);
                this._onProgress(`Error flashing ${fileInfo.destname}: ${err}`, lastPct, false);
                return;
            }
            totalZippedWritten += fileInfo.zippedSize;
            console.log(`RICUpdater: update: totalZippedWritten ${totalZippedWritten} zippedSize ${zippedSize} pct ${Math.floor((totalZippedWritten / zippedSize) * 100)}`);
        }

        // Hard reset
        await this._espLoader.hardReset();

        // Debug
        console.log(`RICUpdater: update: totalZippedWritten ${totalZippedWritten} totalSize ${totalSize}`);

        // Done
        this._onProgress("Main firmware update complete", this.PROGRESS_PC_AT_END, true);

    }
}
